import { useCallback, useState } from 'react'

import {
    createProductProfile,
    deleteProductDocument,
    deleteProductProfile,
    getProductProfile,
    listProductProfiles,
    patchProductProfile,
    ProductProfile,
    ProductProfileCreatePayload,
    ProductProfileListItem,
    ProductProfilePatchPayload,
    ProductDocumentType,
    uploadProductDocument,
} from '../models'

const PAGE_SIZE = 50

export const useProductProfiles = () => {
    const [profiles, setProfiles] = useState<ProductProfileListItem[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [
        selectedProfile,
        setSelectedProfile,
    ] = useState<ProductProfile | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)

    const loadProfiles = useCallback(
        async (params: {
            apartment_id: number
            search?: string
            page?: number
        }) => {
            setLoading(true)
            try {
                const response = await listProductProfiles({
                    ...params,
                    page_size: PAGE_SIZE,
                })
                setProfiles(response.data.results)
                setTotalCount(response.data.count)
                return response.data
            } catch (error) {
                console.error('Error loading product profiles:', error)
                setProfiles([])
                setTotalCount(0)
                return Promise.reject(error)
            } finally {
                setLoading(false)
            }
        },
        [],
    )

    const loadProfileDetail = useCallback(async (profileId: number) => {
        setDetailLoading(true)
        try {
            const response = await getProductProfile(profileId)
            setSelectedProfile(response.data)
            return response.data
        } catch (error) {
            console.error('Error loading product profile:', error)
            setSelectedProfile(null)
            return Promise.reject(error)
        } finally {
            setDetailLoading(false)
        }
    }, [])

    const createProfile = useCallback(
        async (payload: ProductProfileCreatePayload) => {
            const response = await createProductProfile(payload)
            return response.data
        },
        [],
    )

    const updateProfile = useCallback(
        async (profileId: number, payload: ProductProfilePatchPayload) => {
            const response = await patchProductProfile(profileId, payload)
            setSelectedProfile(response.data)
            return response.data
        },
        [],
    )

    const removeProfile = useCallback(async (profileId: number) => {
        await deleteProductProfile(profileId)
        setProfiles((prev) =>
            prev.filter((profile) => profile.id !== profileId),
        )
        setTotalCount((prev) => Math.max(0, prev - 1))
        setSelectedProfile((prev) => (prev?.id === profileId ? null : prev))
    }, [])

    const uploadDocument = useCallback(
        async (
            profileId: number,
            file: File,
            options?: { name?: string; document_type?: ProductDocumentType },
        ) => {
            const response = await uploadProductDocument(
                profileId,
                file,
                options,
            )
            setSelectedProfile((prev) => {
                if (!prev || prev.id !== profileId) {
                    return prev
                }
                return {
                    ...prev,
                    documents: [...prev.documents, response.data],
                }
            })
            setProfiles((prev) =>
                prev.map((profile) =>
                    profile.id === profileId
                        ? {
                              ...profile,
                              document_count: profile.document_count + 1,
                          }
                        : profile,
                ),
            )
            return response.data
        },
        [],
    )

    const removeDocument = useCallback(
        async (profileId: number, documentId: number) => {
            await deleteProductDocument(profileId, documentId)
            setSelectedProfile((prev) => {
                if (!prev || prev.id !== profileId) {
                    return prev
                }
                return {
                    ...prev,
                    documents: prev.documents.filter(
                        (document) => document.id !== documentId,
                    ),
                }
            })
            setProfiles((prev) =>
                prev.map((profile) =>
                    profile.id === profileId
                        ? {
                              ...profile,
                              document_count: Math.max(
                                  0,
                                  profile.document_count - 1,
                              ),
                          }
                        : profile,
                ),
            )
        },
        [],
    )

    const clearSelectedProfile = useCallback(() => {
        setSelectedProfile(null)
    }, [])

    const updateSelectedProfile = useCallback((profile: ProductProfile) => {
        setSelectedProfile(profile)
    }, [])

    return {
        profiles,
        totalCount,
        loading,
        selectedProfile,
        detailLoading,
        loadProfiles,
        loadProfileDetail,
        createProfile,
        updateProfile,
        removeProfile,
        uploadDocument,
        removeDocument,
        clearSelectedProfile,
        updateSelectedProfile,
    }
}
